"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseManifest = void 0;
const fs_extra_1 = __importDefault(require("fs-extra"));
const strip_bom_1 = __importDefault(require("strip-bom"));
/**
 * Abstract base class for manifest wrappers.
 * Provides common functionality for reading, writing, and tracking changes to manifest files.
 *
 * @template T - The manifest type this wrapper handles
 */
class BaseManifest {
    _data;
    _filePath;
    _isDirty = false;
    constructor(data, filePath) {
        this._data = data;
        this._filePath = filePath;
    }
    /**
     * Returns the raw manifest data.
     */
    get data() {
        return this._data;
    }
    /**
     * Returns the file path if the manifest was loaded from a file.
     */
    get filePath() {
        return this._filePath;
    }
    /**
     * Indicates whether the manifest has unsaved changes.
     */
    get isDirty() {
        return this._isDirty;
    }
    /**
     * Marks the manifest as having unsaved changes.
     */
    markDirty() {
        this._isDirty = true;
    }
    /**
     * Saves the manifest to the specified file path or the original file path.
     * @param filePath - Optional path to save to. If not provided, uses the original file path.
     * @throws Error if no file path is available.
     */
    async save(filePath) {
        const targetPath = filePath ?? this._filePath;
        if (!targetPath) {
            throw new Error("No file path specified for saving.");
        }
        await fs_extra_1.default.writeFile(targetPath, this.toJSON(), "utf-8");
        this._filePath = targetPath;
        this._isDirty = false;
    }
    // ============= Static Helpers =============
    /**
     * Reads a JSON file and returns the parsed object.
     * @param filePath - Path to the JSON file
     */
    static async readJsonFile(filePath) {
        const content = await fs_extra_1.default.readFile(filePath, "utf-8");
        // Strip BOM to handle UTF-8 BOM encoded files
        const cleanContent = (0, strip_bom_1.default)(content);
        return JSON.parse(cleanContent);
    }
    /**
     * Reads a JSON file synchronously and returns the parsed object.
     * @param filePath - Path to the JSON file
     */
    static readJsonFileSync(filePath) {
        const content = fs_extra_1.default.readFileSync(filePath, "utf-8");
        // Strip BOM to handle UTF-8 BOM encoded files
        const cleanContent = (0, strip_bom_1.default)(content);
        return JSON.parse(cleanContent);
    }
}
exports.BaseManifest = BaseManifest;
//# sourceMappingURL=BaseManifest.js.map